import { memo } from 'react';
import { useKioskState } from '../../context/Kiosk/kiosk';
import css from './Disclaimer.module.scss';
import { useLocale, useLocalization } from '../../context/Localization/localization';
import { useAppointmentState, useDisclaimerInput, useEstimateSignature } from '../../context/Appointment/appointment';
import { SignatureSmallDisplay } from '../SignatureSmallDisplay/SignatureSmallDisplay';
import { useCart } from '../../context/cart';
import { GMSizedIcon } from '../Icon';
import { GoMotoComponent } from '../../API/GoMoto.types';
import { DisclaimerProps } from './Disclaimer.types';
import { Locales, LocalizationType } from '../../context/Localization/localization.types';
import dayjs from 'dayjs';
import { IAppointment } from '../../API/GoMoto';

export const Disclaimer: GoMotoComponent<DisclaimerProps> = memo(({
    showDisclaimerChoices = false,
}) => {
    const {
        dealer: {
            localization: { disclaimerExtra },
            consentToSignElectronicallyEnabled,
            retainOldPartsEnabled,
            stateEstimateEnabled,
            preliminaryEstimateEnabled,
            name,
        },
    } = useKioskState();
    const { optInTime }: IAppointment = useAppointmentState();
    const Localization: LocalizationType = useLocalization();
    const { minEstCost } = useCart();
    const { gaveConsentToSignElectronically, retainOldParts, estimateRequest, repairCost } = useDisclaimerInput();
    const locale: Locales = useLocale();
    const dateString: string = new Date()
        .toLocaleDateString(locale, {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
        });
    const estimateSignature: string | undefined = useEstimateSignature();

    const localizedDateFormat: string = locale === Locales.fr ? 'YYYY/MM/DD' : 'MM/DD/YYYY';
    const localizedTimeFormat: string = locale === Locales.fr ? 'H [h] mm' : 'h:mm A';

    return (
        <>
            {showDisclaimerChoices ? (
                !consentToSignElectronicallyEnabled && !!gaveConsentToSignElectronically && !retainOldPartsEnabled && !stateEstimateEnabled ? (
                    <div className={css.agreement}>&#8203;</div>
                ) : (
                    <>
                        {consentToSignElectronicallyEnabled && !!gaveConsentToSignElectronically ? (
                            <div className={css.agreement}>
                                <GMSizedIcon iconType={'Complete'} iconSize={'medium'} />
                                <p>{Localization.IAgreeToTheTerms}</p>
                            </div>
                        ) : null}
                        {retainOldPartsEnabled ? (
                            <div className={css.agreement}>
                                {retainOldParts ? (
                                    <>
                                        <GMSizedIcon iconType={'Complete'} iconSize={'medium'} />
                                        <p>{Localization.IDoWantParts}</p>
                                    </>
                                ) : (
                                    <>
                                        <div style={{ width: '28px' }}>
                                            <GMSizedIcon iconType={'Fail'} iconSize={'small'} />
                                        </div>
                                        <p>{Localization.IDoNotWantParts}</p>
                                    </>
                                )}
                            </div>
                        ) : null}
                        {stateEstimateEnabled ? (
                            <div className={css.agreement}>
                                <GMSizedIcon iconType={'Complete'} iconSize={'medium'} />
                                <p>
                                    {estimateRequest === 'yes' ? (
                                        <>{Localization.IRequestAnEstimate}</>
                                    ) : estimateRequest !== 'no' ? (
                                        <>
                                            {Localization.formatString(
                                                Localization.IDoNotRequestAnEstimateUnlessCost,
                                                isNaN(repairCost ?? 0) ? minEstCost : repairCost?.toFixed(2) ?? minEstCost,
                                            )}
                                        </>
                                    ) : (
                                        <>{Localization.IDoNotRequestAnEstimate}</>
                                    )}
                                </p>
                                <div className={css.signedAndDate}>
                                    <div className={css.bottomLine}>
                                        <div>{Localization.Signed}</div>
                                        <div className={css.signedLine}>
                                            <SignatureSmallDisplay />
                                        </div>
                                    </div>
                                    <div className={css.bottomLine}>
                                        <div>{Localization.Date}</div>
                                        <div className={css.dateLine}>
                                            <div className={css.shiftDate}>
                                                {(gaveConsentToSignElectronically && estimateSignature !== undefined) || !consentToSignElectronicallyEnabled
                                                    ? dateString
                                                    : ' '}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ) : null}
                        {optInTime ? (
                            <div className={css.agreement}>
                                <GMSizedIcon iconType={'Complete'} iconSize={'medium'} />
                                <p>
                                    {Localization.formatString(
                                        Localization.OptedInToTextMessaging,
                                        name,
                                        dayjs(optInTime)
                                            .format(localizedDateFormat),
                                        dayjs(optInTime)
                                            .format(localizedTimeFormat),
                                    )}
                                </p>
                            </div>
                        ) : null}
                    </>
                )
            ) : null}
            {preliminaryEstimateEnabled ? (
                <div className={css.disclaimer}>
                    <p>{disclaimerExtra}</p>
                </div>
            ) : null}
        </>
    );
});
